import Cookies from "universal-cookie";
import { hasValidToken } from "../utils/authSession";
import logger from '../utils/logger';



// ====================================================================
// CONSTANTS - All constants must be at the top in UPPERCASE
// ====================================================================

/**
 * All available roles
 */
const ROLES = {
    ROLE_STAFF: 'ROLE_STAFF',
    ROLE_TEAM_LEADER: 'ROLE_TEAM_LEADER',
    ROLE_MARCOM: 'ROLE_MARCOM',
    ROLE_FINANCE: 'ROLE_FINANCE',
    ROLE_HR: 'ROLE_HR',
    ROLE_MANAGEMENT: 'ROLE_MANAGEMENT',
    ROLE_OWNER: 'ROLE_OWNER',
    ROLE_SUPER_ADMIN: 'ROLE_SUPER_ADMIN',
    ROLE_ACCESS_ALL_OFFICE: 'ROLE_ACCESS_ALL_OFFICE',
    ROLE_CAN_SWITCH_USER: 'ROLE_CAN_SWITCH_USER'
};

/**
 * Role hierarchy
 * ROLE_TEAM_LEADER: ROLE_STAFF
 * ROLE_MARCOM: ROLE_STAFF
 * ROLE_FINANCE: ROLE_STAFF
 * ROLE_HR: [ROLE_STAFF, ROLE_MARCOM, ROLE_FINANCE]
 * ROLE_MANAGEMENT: [ROLE_TEAM_LEADER, ROLE_HR, ROLE_ACCESS_ALL_OFFICE]
 * ROLE_OWNER: ROLE_MANAGEMENT
 */
const ROLE_HIERARCHY = {
    ROLE_TEAM_LEADER: ['ROLE_STAFF'],
    ROLE_MARCOM: ['ROLE_STAFF'],
    ROLE_FINANCE: ['ROLE_STAFF'],
    ROLE_HR: ['ROLE_STAFF', 'ROLE_MARCOM', 'ROLE_FINANCE'],
    ROLE_MANAGEMENT: ['ROLE_TEAM_LEADER', 'ROLE_HR', 'ROLE_ACCESS_ALL_OFFICE'],
    ROLE_OWNER: ['ROLE_MANAGEMENT'],
    ROLE_SUPER_ADMIN: ['ROLE_MANAGEMENT']
};

/**
 * Special Access Configuration
 * Allows specific users to bypass role restrictions for certain features
 *
 * Format: Array of user IDs (strings or numbers)
 *
 * Current Special Access Users:
 * ┌─────────────────────────────┬─────────┬─────────────────────────────────┬─────────────────────────────────────────────────┐
 * │ Permission Key              │ User ID │ User                            │ Access Granted                                  │
 * ├─────────────────────────────┼─────────┼─────────────────────────────────┼─────────────────────────────────────────────────┤
 * │ finance_report              │ 149     │ Kumiko Mae Yasuda               │ /finances?tab=reports                           │
 * │ project_timesheet_report    │ 162     │ Intan Alifia                    │ /timesheet?tab=timesheet-report                 │
 * │ project_client_create       │ 276,283,296 │ Rico Tham, Germaine Tan, Alvin | /project-management?tab=clients                 │
 * └─────────────────────────────┴─────────┴─────────────────────────────────┴─────────────────────────────────────────────────┘
 *
 * To find user info: node db.js "SELECT u.id, u.email, pi.first_name, pi.last_name FROM user u LEFT JOIN personal_info pi ON pi.user_id = u.id WHERE u.id = <ID>"
 *
 * Future Migration:
 * This config may be moved to Node.js backend or database for easier maintenance.
 * Suggested table structure:
 *   - id (INT, PK, AUTO_INCREMENT)
 *   - permission_key (VARCHAR(100), INDEX) - e.g., 'finance_report'
 *   - user_id (INT, FK to user.id)
 *   - created_at (DATETIME)
 *   - created_by (INT, FK to user.id)
 */
const SPECIAL_ACCESS_CONFIG = {
    'finance_report': ['149'],              // User 149 (Kumiko Mae Yasuda): Access to Finance Reports tab
    'project_timesheet_report': ['162'],    // User 162 (Intan Alifia): Access to Timesheet Report tab
    'project_client_create': ['276', '283', '296'] // Users 276/283/296: Access to add client in Project Management
};

/**
 * Department to Role mapping for fallback role assignment
 * Used when user roles are not properly set in the database
 */
const DEPARTMENT_ROLE_MAPPING = {
    'Management': [ROLES.ROLE_MANAGEMENT],
    'HR': [ROLES.ROLE_HR],
    'Finance': [ROLES.ROLE_FINANCE],
    'Marketing': [ROLES.ROLE_MARCOM],
    'Marcom': [ROLES.ROLE_MARCOM],
    'IT': [ROLES.ROLE_STAFF],
    'Operations': [ROLES.ROLE_STAFF],
    'Sales': [ROLES.ROLE_STAFF]
};

/**
 * Management emails that should automatically get ROLE_MANAGEMENT access
 * Emergency fallback for specific user emails
 */
const MANAGEMENT_EMAILS = [
    'quan.pham@mediatropy.com',
    'admin@mediatropy.com'
];

/**
 * Role display names for UI presentation
 */
const ROLE_DISPLAY_NAMES = {
    ROLE_STAFF: 'Staff',
    ROLE_TEAM_LEADER: 'Team Leader',
    ROLE_MARCOM: 'Marcom',
    ROLE_FINANCE: 'Finance',
    ROLE_HR: 'HR',
    ROLE_MANAGEMENT: 'Management',
    ROLE_OWNER: 'Owner',
    ROLE_SUPER_ADMIN: 'Super Admin',
    ROLE_ACCESS_ALL_OFFICE: 'All Office Access'
};

/**
 * Role colors for UI badges
 */
const ROLE_COLORS = {
    ROLE_STAFF: '#52c41a',           // green
    ROLE_TEAM_LEADER: '#333333',
    ROLE_MARCOM: '#722ed1',          // purple
    ROLE_FINANCE: '#fa8c16',         // orange
    ROLE_HR: '#eb2f96',              // pink
    ROLE_MANAGEMENT: '#f5222d',      // red
    ROLE_OWNER: '#262626',           // black
    ROLE_SUPER_ADMIN: '#8c8c8c'      // grey
};

// ====================================================================
// IMPLEMENTATION
// ====================================================================

const cookies = new Cookies();

class RoleService {
    constructor() {
        this.aspireAccessSummary = null;
        this.aspireAccessUserKey = null;
    }

    /**
     * Get current user from cookies
     * @returns {Object|null} Current user object or null
     */
    getCurrentUser() {
        const user = cookies.get('userR');
        return user || null;
    }

    /**
     * Get current user's roles
     * @returns {Array} Array of user roles
     */
    getUserRoles() {
        const user = this.getCurrentUser();
        if (!user) {
            return [];
        }
        
        // Check if roles property exists
        if (user.roles) {
            // Handle different role formats
            const roles = user.roles;
            
            // Already an array
            if (Array.isArray(roles)) {
                return roles;
            }
            
            // String - single role, comma-separated roles, or PHP serialized
            if (typeof roles === 'string') {
                // Check if it's PHP serialized format (e.g., a:1:{i:0;s:10:"ROLE_OWNER";})
                if (roles.startsWith('a:') && roles.includes('{')) {
                    const parsedRoles = [];
                    const regex = /s:\d+:"(ROLE_[A-Z_]+)"/g;
                    let match;
                    while ((match = regex.exec(roles)) !== null) {
                        parsedRoles.push(match[1]);
                    }
                    return parsedRoles;
                }
                // Check if it's comma-separated
                else if (roles.includes(',')) {
                    return roles.split(',').map(role => role.trim());
                }
                // Single role
                else if (roles.startsWith('ROLE_')) {
                    return [roles];
                }
                return [];
            }
            
            // Object with values (e.g., {0: 'ROLE_STAFF', 1: 'ROLE_ADMIN'})
            if (typeof roles === 'object' && roles !== null) {
                // Try to convert object to array
                try {
                    const roleArray = Object.values(roles);
                    if (roleArray.every(role => typeof role === 'string')) {
                        return roleArray;
                    }
                } catch (e) {
                    logger.error('Error converting roles object to array:', e);
                }
            }
        }
        
        // Fallback - try to infer role from department (temporary fix)
        if (user.department || user.department_id) {
            // Also check for specific user emails that should have management access
            if (user.email && MANAGEMENT_EMAILS.includes(user.email.toLowerCase())) {
                logger.warn(`Management role inferred from email "${user.email}"`);
                return [ROLES.ROLE_MANAGEMENT];
            }
            
            const inferredRoles = DEPARTMENT_ROLE_MAPPING[user.department];
            if (inferredRoles) {
                logger.warn(`Roles inferred from department "${user.department}":`, inferredRoles);
                return inferredRoles;
            }
        }
        
        // Default to ROLE_STAFF if no roles found
        logger.warn('No roles found for user, defaulting to ROLE_STAFF');
        return [ROLES.ROLE_STAFF];
    }

    /**
     * Get all inherited roles for a given role
     * @param {string} role - The role to check
     * @returns {Array} Array of all roles (including inherited)
     */
    getInheritedRoles(role) {
        const inherited = new Set([role]);
        const queue = [role];
        
        while (queue.length > 0) {
            const currentRole = queue.shift();
            const inheritedRoles = ROLE_HIERARCHY[currentRole] || [];
            
            inheritedRoles.forEach(inheritedRole => {
                if (!inherited.has(inheritedRole)) {
                    inherited.add(inheritedRole);
                    queue.push(inheritedRole);
                }
            });
        }
        
        return Array.from(inherited);
    }

    /**
     * Get all effective roles for the current user (including inherited)
     * @returns {Array} Array of all effective roles
     */
    getEffectiveRoles() {
        const userRoles = this.getUserRoles();
        const effectiveRoles = new Set();
        
        
        // Ensure userRoles is an array before using forEach
        if (Array.isArray(userRoles)) {
            userRoles.forEach(role => {
                this.getInheritedRoles(role).forEach(inheritedRole => {
                    effectiveRoles.add(inheritedRole);
                });
            });
        }
        
        return Array.from(effectiveRoles);
    }

    /**
     * Check if user has a specific role (including inherited)
     * @param {string} role - The role to check
     * @returns {boolean} True if user has the role
     */
    hasRole(role) {
        const effectiveRoles = this.getEffectiveRoles();
        return effectiveRoles.includes(role);
    }

    /**
     * Check if user has a directly assigned role, without hierarchy expansion
     * @param {string} role - The role to check
     * @returns {boolean} True if user has the role directly assigned
     */
    hasDirectRole(role) {
        return this.getUserRoles().includes(role);
    }

    /**
     * Check if user has any directly assigned role, without hierarchy expansion
     * @param {Array} roles - Array of roles to check
     * @returns {boolean} True if user has any role directly assigned
     */
    hasAnyDirectRole(roles) {
        return roles.some(role => this.hasDirectRole(role));
    }

    /**
     * Check if user has any of the specified roles
     * @param {Array} roles - Array of roles to check
     * @returns {boolean} True if user has any of the roles
     */
    hasAnyRole(roles) {
        return roles.some(role => this.hasRole(role));
    }

    /**
     * Check if user has all of the specified roles
     * @param {Array} roles - Array of roles to check
     * @returns {boolean} True if user has all of the roles
     */
    hasAllRoles(roles) {
        return roles.every(role => this.hasRole(role));
    }

    /**
     * Check if user is authenticated
     * @returns {boolean} True if user is authenticated
     */
    isAuthenticated() {
        const token = cookies.get("tokenR");
        return this.getCurrentUser() !== null && hasValidToken(token);
    }

    /**
     * Role-specific helper methods
     */
    
    isStaff() {
        return this.hasRole(ROLES.ROLE_STAFF);
    }

    isTeamLeader() {
        return this.hasRole(ROLES.ROLE_TEAM_LEADER);
    }

    isMarcom() {
        return this.hasRole(ROLES.ROLE_MARCOM);
    }

    isFinance() {
        return this.hasRole(ROLES.ROLE_FINANCE);
    }

    isHR() {
        return this.hasRole(ROLES.ROLE_HR);
    }

    isManagement() {
        return this.hasRole(ROLES.ROLE_MANAGEMENT);
    }

    isOwner() {
        return this.hasRole(ROLES.ROLE_OWNER);
    }

    hasAllOfficeAccess() {
        return this.hasRole(ROLES.ROLE_ACCESS_ALL_OFFICE);
    }

    /**
     * Check if user can unarchive projects
     * Only ROLE_OWNER, ROLE_MANAGEMENT, and ROLE_FINANCE (but NOT ROLE_HR even though it inherits ROLE_FINANCE)
     * @returns {boolean} True if user can unarchive projects
     */
    canUnarchiveProject() {
        // Get user's direct roles (not inherited/effective roles)
        const directRoles = this.getUserRoles();

        // Check if user has ROLE_OWNER, ROLE_MANAGEMENT, or ROLE_FINANCE in their direct roles
        // This prevents ROLE_HR from getting access even though it inherits ROLE_FINANCE
        return directRoles.includes(ROLES.ROLE_OWNER) ||
               directRoles.includes(ROLES.ROLE_MANAGEMENT) ||
               directRoles.includes(ROLES.ROLE_FINANCE);
    }

    /**
     * Special Access System
     * Allows specific users to bypass role restrictions for certain features
     *
     * @param {string} permissionKey - The permission key (e.g., 'finance_report')
     * @returns {boolean} True if current user has special access to the feature
     */
    hasSpecialAccess(permissionKey) {
        const currentUser = this.getCurrentUser();
        if (!currentUser) return false;

        const allowedUserIds = SPECIAL_ACCESS_CONFIG[permissionKey];
        if (!allowedUserIds) return false;

        // Support both array and comma-separated string formats
        // Array format is preferred and will be used when migrating to database
        let allowedIds;
        if (Array.isArray(allowedUserIds)) {
            allowedIds = allowedUserIds.map(id => String(id));
        } else {
            // Legacy: comma-separated string (backward compatible)
            allowedIds = allowedUserIds.split(',').map(id => id.trim());
        }

        return allowedIds.includes(String(currentUser.id));
    }

    /**
     * Combined permission check - Role OR Special Access
     *
     * @param {string} role - Required role (e.g., ROLES.ROLE_FINANCE)
     * @param {string} specialAccessKey - Special access permission key
     * @returns {boolean} True if user has role OR special access
     */
    hasRoleOrSpecialAccess(role, specialAccessKey) {
        return this.hasRole(role) || this.hasSpecialAccess(specialAccessKey);
    }

    /**
     * Check if user can access financial data
     * @returns {boolean} True if user can access financial data
     */
    canAccessFinancialData() {
        return this.hasAnyRole([
            ROLES.ROLE_FINANCE,
            ROLES.ROLE_HR,
            ROLES.ROLE_MANAGEMENT,
            ROLES.ROLE_OWNER
        ]);
    }

    /**
     * Check if user can manage HR functions
     * @returns {boolean} True if user can manage HR
     */
    canManageHR() {
        return this.hasAnyRole([
            ROLES.ROLE_HR,
            ROLES.ROLE_MANAGEMENT,
            ROLES.ROLE_OWNER
        ]);
    }

    /**
     * Check if user can access appraisal admin pages
     * @returns {boolean} True if user has HR hierarchy access
     */
    canManageAppraisals() {
        return this.canManageHR();
    }

    /**
     * Check if user can manage teams
     * @returns {boolean} True if user can manage teams
     */
    canManageTeams() {
        return this.hasAnyRole([
            ROLES.ROLE_TEAM_LEADER,
            ROLES.ROLE_MANAGEMENT,
            ROLES.ROLE_OWNER
        ]);
    }

    canAccessAspireInvoiceAllocation() {
        if (this.hasRole(ROLES.ROLE_FINANCE) || this.hasRole(ROLES.ROLE_SUPER_ADMIN)) {
            return true;
        }
        return this.getCurrentAspireAccessSummary()?.canAccess === true;
    }

    canProcessAspireInvoiceAllocation() {
        if (this.hasRole(ROLES.ROLE_OWNER) || this.hasRole(ROLES.ROLE_SUPER_ADMIN) || this.hasDirectRole(ROLES.ROLE_FINANCE)) {
            return true;
        }
        return this.getCurrentAspireAccessSummary()?.canProcess === true;
    }

    canManageAspireCardAccess() {
        if (this.hasRole(ROLES.ROLE_OWNER) || this.hasRole(ROLES.ROLE_SUPER_ADMIN)) {
            return true;
        }
        return this.getCurrentAspireAccessSummary()?.canManage === true;
    }

    setAspireAccessSummary(summary) {
        this.aspireAccessSummary = summary && typeof summary === 'object'
            ? {
                canAccess: summary.canAccess === true,
                canProcess: summary.canProcess === true,
                canManage: summary.canManage === true,
            }
            : null;
        this.aspireAccessUserKey = this.aspireAccessSummary ? this.getAspireAccessUserKey() : null;
    }

    getAspireAccessUserKey() {
        const currentUser = this.getCurrentUser();
        if (currentUser?.id !== undefined && currentUser?.id !== null) {
            return `id:${currentUser.id}`;
        }
        const email = String(currentUser?.email || '').trim().toLowerCase();
        return email ? `email:${email}` : null;
    }

    getCurrentAspireAccessSummary() {
        if (!this.aspireAccessUserKey || this.aspireAccessUserKey !== this.getAspireAccessUserKey()) {
            return null;
        }
        return this.aspireAccessSummary;
    }

    /**
     * Check if user has administrative privileges
     * @returns {boolean} True if user is admin
     */
    isAdmin() {
        return this.hasAnyRole([
            ROLES.ROLE_MANAGEMENT,
            ROLES.ROLE_OWNER
        ]);
    }

    /**
     * Get highest role for the current user
     * @returns {string|null} Highest role or null
     */
    getHighestRole() {
        const roleOrder = [
            ROLES.ROLE_OWNER,
            ROLES.ROLE_MANAGEMENT,
            ROLES.ROLE_HR,
            ROLES.ROLE_FINANCE,
            ROLES.ROLE_MARCOM,
            ROLES.ROLE_TEAM_LEADER,
            ROLES.ROLE_STAFF
        ];

        const userRoles = this.getEffectiveRoles();

        for (const role of roleOrder) {
            if (userRoles.includes(role)) {
                return role;
            }
        }

        return null;
    }

    /**
     * Get public role display name for a user
     * Returns the highest priority role in human-readable format
     *
     * Priority order (highest to lowest):
     * 1. ROLE_OWNER → "Owner"
     * 2. ROLE_MANAGEMENT → "Management"
     * 3. ROLE_HR → "HR"
     * 4. ROLE_TEAM_LEADER → "Team Leader"
     * 5. ROLE_FINANCE → "Finance"
     * 6. ROLE_MARCOM → "Marcom"
     * 7. default → "Staff"
     *
     * @param {Array} roles - Array of user roles (e.g., ['ROLE_STAFF', 'ROLE_HR'])
     * @returns {string} Display name of the highest priority role
     */
    getPublicRoleName(roles) {
        if (!roles || !Array.isArray(roles) || roles.length === 0) {
            return 'Staff';
        }

        // Check roles in priority order (highest first)
        if (roles.includes(ROLES.ROLE_OWNER)) return ROLE_DISPLAY_NAMES.ROLE_OWNER;
        if (roles.includes(ROLES.ROLE_MANAGEMENT)) return ROLE_DISPLAY_NAMES.ROLE_MANAGEMENT;
        if (roles.includes(ROLES.ROLE_HR)) return ROLE_DISPLAY_NAMES.ROLE_HR;
        if (roles.includes(ROLES.ROLE_TEAM_LEADER)) return ROLE_DISPLAY_NAMES.ROLE_TEAM_LEADER;
        if (roles.includes(ROLES.ROLE_FINANCE)) return ROLE_DISPLAY_NAMES.ROLE_FINANCE;
        if (roles.includes(ROLES.ROLE_MARCOM)) return ROLE_DISPLAY_NAMES.ROLE_MARCOM;

        return 'Staff';
    }

    /**
     * Get role display name
     * @param {string} role - The role
     * @returns {string} Display name
     */
    getRoleDisplayName(role) {
        return ROLE_DISPLAY_NAMES[role] || role;
    }

    /**
     * Get color for role badge
     * @param {string} role - The role
     * @returns {string} Color code
     */
    getRoleColor(role) {
        return ROLE_COLORS[role] || '#8c8c8c';
    }

    /**
     * Check if user is a manager
     * Manager = has isManager database flag (is_manager=1) OR has ROLE_MANAGEMENT/ROLE_OWNER
     * @returns {boolean} True if user is manager
     */
    isManager() {
        const currentUser = this.getCurrentUser();
        if (!currentUser) return false;

        // Check database flag (is_manager=1)
        const hasManagerFlag = currentUser.isManager || false;

        // Fallback to role-based check if isManager field is not available (backward compatibility)
        const isManagerByRole = this.hasRole(ROLES.ROLE_MANAGEMENT) || this.hasRole(ROLES.ROLE_OWNER);

        // Use database flag if available, otherwise fall back to role-based
        return Object.prototype.hasOwnProperty.call(currentUser, 'isManager') ? hasManagerFlag : isManagerByRole;
    }
}

// Export singleton instance
const roleService = new RoleService();
export default roleService;

// Also export the ROLES constant for easy access
export { ROLES };
